import { SolidButton } from "@/components/StoreComponents/StoreButton";
import { useAppSelector } from "@/redux/hooks";
import { StoreBrandingType } from "@/types/misc";
import useStoreType from "@/utils/hooks/useStoreType";
import {
  formatMoney,
  FourPointStarIcon,
  getDefaultBrandclubImage,
  getEntityImage,
  TABLET_SCREEN_SIZE,
} from "@brandclub/common-ui";
import { alpha, Box, styled } from "@mui/material";

export const RewardExpandContainer = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  width: "100%",
  gap: 16,
  marginTop: 31,
  [theme.breakpoints.down("md")]: {
    marginTop: 20,
    gap: 8,
    gridTemplateColumns: "1fr",
  },
}));

const BenefitsContent = styled("div")(({ theme }) => ({
  overflowY: "auto",
  flex: 1,
  paddingBottom: 12,
  paddingTop: 12,
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  minWidth: 280,
  // maxWidth: 20,
  // width: "100%",

  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    gap: "5px",
  },
  "&::-webkit-scrollbar": {
    width: 4,
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    background: alpha(theme.palette.primary.main, 0.2),
    borderRadius: 2,
  },
}));

const BenefitCardRow = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: 16,
  padding: "2px 2px",
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    gap: 10,
  },
}));

const BenefitCardTitle = styled("div")(({ theme }) => ({
  fontWeight: 600,
  fontSize: 16,
  color: theme.vars.palette.text.primary,
  marginBottom: 2,
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    fontSize: 14,
  },
}));

export const ConnectBenefits = ({
  rewardAmount,
  retailerId,
}: {
  rewardAmount?: number;
  retailerId?: number;
}) => {
  const retailerNameMap = useAppSelector((state) => state.retailerNameMap);
  const retailerName = retailerId ? retailerNameMap[retailerId] || "retailer" : "retailer";

  const benefitList = [
    rewardAmount && rewardAmount > 0
      ? `${formatMoney(rewardAmount)} in cash rewards`
      : "Earn cashback rewards",
    `Fast, free shipping from ${retailerName}`,
    "Hassle-free returns",
    "Securely connect in seconds",
  ];

  return (
    <BenefitsContent>
      {benefitList.map((benefit) => (
        <BenefitCardRow>
          <FourPointStarIcon sx={{ width: 16, height: 16 }} />
          <BenefitCardTitle>{benefit}</BenefitCardTitle>
        </BenefitCardRow>
      ))}
    </BenefitsContent>
  );
};

const HeaderContainer = styled("div")(({ theme }) => ({
  marginTop: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexDirection: "column",
  gap: 12,
  [theme.breakpoints.down("md")]: {
    marginTop: 12,
  },

  ".icons": {
    display: "flex",
    alignItems: "center",
    marginBottom: 12,
    gap: 32,
    [theme.breakpoints.down("md")]: {
      gap: 6,
    },

    ".icons-groups": {
      display: "flex",
      alignItems: "center",
      gap: 32,
      position: "relative",

      width: 64,
      height: 64,
      [theme.breakpoints.down("md")]: {
        width: 52,
        height: 52,
      },
      ".layer-1": {
        position: "absolute",
        zIndex: 3,
        top: 0,
        left: -50,
        [theme.breakpoints.down("md")]: {
          left: -44,
        },
      },
      ".layer-2": {
        position: "absolute",
        zIndex: 2,
        top: 0,
        left: 0,
      },
      ".layer-3": {
        position: "absolute",
        zIndex: 1,
        top: 0,
        left: 50,
        [theme.breakpoints.down("md")]: {
          left: 44,
        },
      },
    },
    ".icon": {
      width: 64,
      backgroundColor: "#ffffff",
      height: 64,
      border: "2px solid #ffffff",
      borderRadius: "999px",
      [theme.breakpoints.down("md")]: {
        width: 52,
        height: 52,
      },
      img: {
        borderRadius: 999,
        width: "100%",
        height: "100%",
        objectFit: "contain",
      },
    },
  },
  ".header-title": {
    fontSize: 20,
    fontWeight: 700,
    color: theme.vars.palette.text.primary,
    textAlign: "center",
    maxWidth: 380,
  },
  ".header-description": {
    fontSize: 16,
    color: theme.vars.palette.text.primary,
    textAlign: "center",
    maxWidth: 380,
  },
}));

const Header = ({ retailerId }: { retailerId?: number }) => {
  const storeType = useStoreType();

  const checkoutLogo = useAppSelector(
    (state) => state.appConfig?.checkoutPageConfig?.logoPicture
  );
  const domainConfig = useAppSelector(
    (state) => state?.appConfig?.domainConfig
  );
  const retailerNameMap = useAppSelector((state) => state.retailerNameMap);
  const isDtc = storeType === StoreBrandingType.CustomDTCStore;

  const dtcBrandName = domainConfig?.brandName;

  const retailerName = retailerId ? retailerNameMap[retailerId] || "retailer" : "retailer";

  const dtcBrandLogo = domainConfig?.brandId
    ? checkoutLogo || getDefaultBrandclubImage(domainConfig?.brandId)
    : null;

  return (
    <HeaderContainer>
      <div className="icons">
        {isDtc && dtcBrandLogo ? (
          <div className="icons-groups">
            <div className="icon layer-1">
              <img src={dtcBrandLogo} alt="retailer connect" />
            </div>
            <div className="icon layer-2">
              <img
                src="https://media.brandclub.com/brandclub/image_asset/brandclub-b-logo.webp"
                alt="retailer connect"
              />
            </div>
            {retailerId ? (
              <div className="icon layer-3">
                <img
                  src={getEntityImage(retailerId, "retailer")}
                  alt="retailer connect"
                />
              </div>
            ) : null}
          </div>
        ) : (
          <div className="icon">
            <img
              src="https://media.brandclub.com/brandclub/image_asset/brandclub-b-logo.webp"
              alt="retailer connect"
            />
          </div>
        )}
      </div>
      <div className="header-title">
        {isDtc
          ? `${dtcBrandName} uses Brandclub to connect your ${retailerName} account`
          : `Use Brandclub to connect your ${retailerName} account`}
      </div>

      <div className="header-description">
        Join millions of shoppers that use Brandclub.
      </div>
    </HeaderContainer>
  );
};

const AiShopContainer = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: 12,
  fontSize: 14,
  backgroundColor: "#f6f8fa",
  borderRadius: 8,
  padding: "16px 35px",
  textAlign: "center",
  boxSizing: "border-box",

  marginTop: 12,
  maxWidth: 380,
  [theme.breakpoints.down("md")]: {
    padding: "16px 20px",
    maxWidth: "100%",
  },
}));

const AiShop = () => {
  return (
    <AiShopContainer>
      Our AI concierge will manage your order and maximize your rewards.
    </AiShopContainer>
  );
};

const Container = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  lineHeight: "24pxg",
  gap: 12,
}));

export const RetailerConnectBenefits = ({
  rewardAmount,
  retailerId,
  onContinue,
}: {
  rewardAmount?: number;
  retailerId?: number;
  onContinue: () => void;
}) => {
  return (
    <Container>
      <Header retailerId={retailerId} />
      <ConnectBenefits rewardAmount={rewardAmount} retailerId={retailerId} />

      <SolidButton
        className="checkout-solid-button"
        sx={(theme) => ({
          width: "100%",
          maxWidth: 380,
          height: 50,
          fontSize: 16,
          [theme.breakpoints.down("md")]: {
            maxWidth: "100%",
          },
        })}
        onClick={onContinue}
      >
        Continue
      </SolidButton>
      <AiShop />
    </Container>
  );
};
